/**
 * The payoff shape of a strategy, drawn from its legs — ROADMAP 5.1/5.7.
 *
 * DERIVED, NEVER STORED. An earlier draft of 5.1 gave each template a payoff
 * glyph as data. A saved picture can disagree with the position it claims to
 * describe: edit a leg's offset and the glyph silently lies about what will
 * be traded, on the screen used to choose what to trade. Computing it from
 * the legs makes the two incapable of disagreeing.
 *
 * It is a SHAPE, not a valuation. Premium is deliberately ignored — the curve
 * shows the structure (where it profits, where it is capped, where it is
 * open-ended), not what it is worth. Including premium would need live prices
 * for a 40×20 pixel icon, and would make the icon move while a user reads the
 * list.
 */

import type { StrategyDto } from '@/types/strategy'

/** Intrinsic value at expiry of one leg, per unit, ignoring premium paid. */
function legPayoff(
  leg: StrategyDto['legs'][number],
  spotOffset: number,
  stepsOf: (leg: StrategyDto['legs'][number]) => number,
): number {
  const strike = stepsOf(leg)
  const intrinsic =
    leg.optionType === 'CE' ? Math.max(0, spotOffset - strike) : Math.max(0, strike - spotOffset)
  // A sold option pays its holder, so it is negative to us.
  return (leg.side === 'BUY' ? 1 : -1) * intrinsic * leg.ratio
}

/**
 * An SVG path for the payoff curve, in a `w`×`h` box.
 *
 * The x axis is strike offsets from ATM, not rupees, which is the same unit
 * the legs are written in — so the drawing needs no price, no step and no
 * underlying, and one glyph is correct for every index.
 */
export function payoffPath(legs: StrategyDto['legs'], w: number, h: number): string {
  /*
   * A premium-selected leg has no offset until it is resolved against a live
   * chain, and this runs on a list that may not be resolved yet. Treated as
   * ATM, which draws the right FAMILY of shape — a strangle still looks like
   * a strangle — without pretending to know the strike.
   */
  const stepsOf = (leg: StrategyDto['legs'][number]): number =>
    leg.strikeCriteria?.kind === 'ATM_OFFSET' ? leg.strikeCriteria.steps : 0

  const offsets = legs.map(stepsOf)
  const spread = Math.max(2, ...offsets.map((o) => Math.abs(o))) + 2
  const SAMPLES = 48

  const points: { x: number; y: number }[] = []
  for (let i = 0; i <= SAMPLES; i += 1) {
    const offset = -spread + (2 * spread * i) / SAMPLES
    const value = legs.reduce((sum, leg) => sum + legPayoff(leg, offset, stepsOf), 0)
    points.push({ x: i / SAMPLES, y: value })
  }

  const ys = points.map((p) => p.y)
  const lo = Math.min(...ys)
  const hi = Math.max(...ys)
  // A flat payoff — no legs, or legs that cancel — is a flat line rather than
  // a division by zero.
  const span = hi - lo || 1

  const pad = 2
  const usable = h - pad * 2

  return points
    .map((p, i) => {
      const x = (p.x * w).toFixed(2)
      // SVG y grows downward, so profit must be subtracted from the bottom.
      const y = (h - pad - ((p.y - lo) / span) * usable).toFixed(2)
      return `${i === 0 ? 'M' : 'L'}${x},${y}`
    })
    .join(' ')
}
